import React, { type FC } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { type DateAfter, type DateRange, DayPicker } from 'react-day-picker'

import { CALENDAR_CLASS_NAMES, CALENDAR_CSS_VARIABLES, CALENDAR_OPEN_BOUND_CLASS_NAME, CALENDAR_SELECTORS } from './Constant'

export type CalendarProps = Readonly<{
    /** The selected date range. */
    selectedRange?: DateRange
    /** Called when the selected date range changes. */
    onRangeSelect: (range: DateRange | undefined) => void
    /** The starting month shown in the two-month calendar view. */
    displayMonth: Date
    /** Called when navigating to a different month. */
    onMonthChange: (month: Date) => void
    /** The cutoff date used to disable dates after it. */
    disableAfter?: DateAfter['after']
}>

const calendarStyle: SxProps<Theme> = (theme: Theme) => ({
    position: 'relative',
    [`& ${CALENDAR_SELECTORS.ROOT}`]: {
        padding: 3,
        [CALENDAR_CSS_VARIABLES.ACCENT_BACKGROUND_COLOR]: theme.palette.primary.light,
        [CALENDAR_CSS_VARIABLES.DAY_WIDTH]: '32px',
        [CALENDAR_CSS_VARIABLES.DAY_HEIGHT]: '32px',
        [CALENDAR_CSS_VARIABLES.DAY_BUTTON_WIDTH]: '32px',
        [CALENDAR_CSS_VARIABLES.DAY_BUTTON_HEIGHT]: '32px',
        [CALENDAR_CSS_VARIABLES.NAV_HEIGHT]: '28px',
        [CALENDAR_CSS_VARIABLES.NAV_BUTTON_WIDTH]: '28px',
        [CALENDAR_CSS_VARIABLES.NAV_BUTTON_HEIGHT]: '28px',
        [CALENDAR_CSS_VARIABLES.ACCENT_COLOR]: theme.palette.grey[700],
        [CALENDAR_CSS_VARIABLES.RANGE_START_DATE_BACKGROUND_COLOR]: theme.palette.primary[600],
        [CALENDAR_CSS_VARIABLES.RANGE_END_DATE_BACKGROUND_COLOR]: theme.palette.primary[600],
        [CALENDAR_CSS_VARIABLES.RANGE_MIDDLE_BACKGROUND_COLOR]: theme.palette.primary[50],
        [CALENDAR_CSS_VARIABLES.SELECTED_BORDER]: 'unset',
        [CALENDAR_CSS_VARIABLES.TODAY_COLOR]: theme.palette.primary[600],
        [CALENDAR_CSS_VARIABLES.WEEKDAY_PADDING]: 0,
        [CALENDAR_CSS_VARIABLES.WEEKDAY_OPACITY]: 'unset',
    },
    [`& ${CALENDAR_SELECTORS.DAY}`]: { position: 'relative', padding: 1 },
    [`& ${CALENDAR_SELECTORS.DAY_BUTTON}`]: { position: 'relative', zIndex: 1, borderRadius: '50%' },
    [`& ${CALENDAR_SELECTORS.SELECTED}`]: { fontWeight: 'unset', fontSize: theme.typography.body1.fontSize },
    [`& ${CALENDAR_SELECTORS.MONTH_CAPTION}`]: { justifyContent: 'center', mb: 5 },
    [`& ${CALENDAR_SELECTORS.CAPTION_LABEL}`]: { ...theme.typography.subtitle2, fontWeight: 500, color: 'grey.900' },
    [`& ${CALENDAR_SELECTORS.WEEKDAY}`]: { ...theme.typography.caption, fontWeight: 600, color: 'grey.700', height: 32 },
    [`& ${CALENDAR_SELECTORS.CHEVRON}`]: { width: 16, height: 16 },
    [`& ${CALENDAR_SELECTORS.TODAY} ${CALENDAR_SELECTORS.DAY_BUTTON}`]: { border: '1.5px solid', borderColor: 'primary.600' },
    // An open-started/open-ended range has only one bound, so its day is never both range_start and range_end -
    // give its day button the same bright fill a range boundary's day button gets, instead of no highlight at all
    [`& ${CALENDAR_SELECTORS.OPEN_BOUND} ${CALENDAR_SELECTORS.DAY_BUTTON}`]: { backgroundColor: 'primary.600', color: 'common.white' },

    [`& ${CALENDAR_SELECTORS.RANGE_START}, & ${CALENDAR_SELECTORS.RANGE_MIDDLE}, & ${CALENDAR_SELECTORS.RANGE_END}`]: { background: 'none' },
    // inset: 0 (rather than insetting the top/bottom) keeps this pseudo-element exactly as tall as the day cell/button,
    // so it stays square and its rounded ends below form a true circle matching the day button's - insetting the
    // vertical edges would make it wider than tall, turning those rounded ends into ellipses that peek out from behind
    // the circular day button at the range's start/end
    [`& ${CALENDAR_SELECTORS.RANGE_START}::before, & ${CALENDAR_SELECTORS.RANGE_MIDDLE}::before, & ${CALENDAR_SELECTORS.RANGE_END}::before`]: {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: theme.palette.primary[100],
    },
    [`& ${CALENDAR_SELECTORS.RANGE_START}${CALENDAR_SELECTORS.RANGE_END}::before`]: { display: 'none' },
    [`& ${CALENDAR_SELECTORS.RANGE_START}::before, & ${CALENDAR_SELECTORS.DAY}:first-of-type::before`]: {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
    },
    [`& ${CALENDAR_SELECTORS.RANGE_END}::before, & ${CALENDAR_SELECTORS.DAY}:last-of-type::before`]: {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
    },
})

const dividerStyle: SxProps<Theme> = {
    position: 'absolute',
    left: '50%',
    top: '20%',
    bottom: 0,
    width: '1px',
    bgcolor: 'divider',
    transform: 'translateX(-50%)',
}

// The sole day of an open-started/open-ended range (only one of `from`/`to` defined) - react-day-picker only
// marks a day as range_start/range_end when *both* bounds are defined, so that day would otherwise get no
// highlight at all; see the `cal-open-bound` modifier this feeds into `Calendar`'s CSS.
const getOpenBoundDay = (selectedRange: DateRange | undefined): Date | undefined => {
    if (!selectedRange) return undefined
    const { from, to } = selectedRange
    return Boolean(from) !== Boolean(to) ? from ?? to : undefined
}

export const Calendar: FC<CalendarProps> = React.memo(({ selectedRange, onRangeSelect, displayMonth, onMonthChange, disableAfter }) => {
    const openBoundDay = getOpenBoundDay(selectedRange)

    return (
        <Box sx={calendarStyle} data-testid="calendar-picker">
            <DayPicker
                mode="range"
                classNames={CALENDAR_CLASS_NAMES}
                modifiers={{ openBound: openBoundDay ?? [] }}
                modifiersClassNames={{ openBound: CALENDAR_OPEN_BOUND_CLASS_NAME }}
                selected={selectedRange}
                onSelect={onRangeSelect}
                month={displayMonth}
                onMonthChange={onMonthChange}
                numberOfMonths={2}
                weekStartsOn={1}
                fixedWeeks
                navLayout="around"
                disabled={disableAfter ? { after: disableAfter } : undefined}
                resetOnSelect
            />
            <Box sx={dividerStyle} />
        </Box>
    )
})
